/**
 * Lightweight formatting for Office sidebar AI replies (lists, bold, paragraphs).
 * Avoids a markdown dependency in the taskpane bundle.
 */
import React, { useMemo } from 'react';

type InlinePart =
    | { type: 'text'; value: string }
    | { type: 'bold'; value: string }
    | { type: 'italic'; value: string }
    | { type: 'code'; value: string };

type Block =
    | { type: 'paragraph'; parts: InlinePart[] }
    | { type: 'ol'; items: InlinePart[][] }
    | { type: 'ul'; items: InlinePart[][] };

/** Insert breaks when the model glues "1. **A** … 2. **B**" on one line. */
export function normalizeAssistantContent(raw: string): string {
    if (!raw) return '';
    let text = raw.replace(/\r\n/g, '\n').trim();

    // Numbered items jammed after punctuation / mid-line (e.g. ":1. **Title**")
    text = text.replace(/([:;.!?])\s*(?=\d{1,2}\.\s+)/g, '$1\n');
    text = text.replace(/([^\n])\s+(?=\d{1,2}\.\s+\*\*)/g, '$1\n');
    text = text.replace(/([^\n])\s+(?=\d{1,2}\.\s+[A-ZÀ-ÖØ-Ý])/g, '$1\n');
    // Also when model omits space: "pratiques :1."
    text = text.replace(/([^\n\d.])(?=\d{1,2}\.\s+\*\*)/g, '$1\n');

    // Bullet items jammed mid-line
    text = text.replace(/([:;.!?])\s*(?=[-•*]\s+)/g, '$1\n');
    text = text.replace(/([^\n])\s+(?=[-•*]\s+\*\*)/g, '$1\n');
    text = text.replace(/([^\n])\s+(?=[-•*]\s+[A-ZÀ-ÖØ-Ý])/g, '$1\n');

    // Collapse 3+ blank lines
    text = text.replace(/\n{3,}/g, '\n\n');
    return text;
}

function parseInline(text: string): InlinePart[] {
    const parts: InlinePart[] = [];
    // **bold**, *italic*, `code` — non-greedy, no nesting
    const re = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        if (match.index > last) {
            parts.push({ type: 'text', value: text.slice(last, match.index) });
        }
        const token = match[0];
        if (token.startsWith('**') && token.endsWith('**')) {
            parts.push({ type: 'bold', value: token.slice(2, -2) });
        } else if (token.startsWith('`') && token.endsWith('`')) {
            parts.push({ type: 'code', value: token.slice(1, -1) });
        } else if (token.startsWith('*') && token.endsWith('*')) {
            parts.push({ type: 'italic', value: token.slice(1, -1) });
        } else {
            parts.push({ type: 'text', value: token });
        }
        last = match.index + token.length;
    }
    if (last < text.length) {
        parts.push({ type: 'text', value: text.slice(last) });
    }
    return parts.length ? parts : [{ type: 'text', value: text }];
}

function parseBlocks(text: string): Block[] {
    const lines = text.split('\n');
    const blocks: Block[] = [];
    let paraBuf: string[] = [];
    let listType: 'ol' | 'ul' | null = null;
    let listItems: InlinePart[][] = [];

    const flushPara = () => {
        if (!paraBuf.length) return;
        const joined = paraBuf.join(' ').replace(/\s+/g, ' ').trim();
        if (joined) {
            blocks.push({ type: 'paragraph', parts: parseInline(joined) });
        }
        paraBuf = [];
    };

    const flushList = () => {
        if (!listType || !listItems.length) {
            listType = null;
            listItems = [];
            return;
        }
        blocks.push({ type: listType, items: listItems });
        listType = null;
        listItems = [];
    };

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
            flushList();
            flushPara();
            continue;
        }

        const ol = trimmed.match(/^(\d{1,2})[.)]\s+(.+)$/);
        const ul = trimmed.match(/^[-•*]\s+(.+)$/);

        if (ol) {
            flushPara();
            if (listType && listType !== 'ol') flushList();
            listType = 'ol';
            listItems.push(parseInline(ol[2]));
            continue;
        }
        if (ul) {
            flushPara();
            if (listType && listType !== 'ul') flushList();
            listType = 'ul';
            listItems.push(parseInline(ul[1]));
            continue;
        }

        // Continuation of previous list item
        if (listType && /^\s{2,}/.test(line) && listItems.length) {
            const prev = listItems[listItems.length - 1];
            listItems[listItems.length - 1] = [
                ...prev,
                { type: 'text', value: ' ' },
                ...parseInline(trimmed),
            ];
            continue;
        }

        flushList();
        paraBuf.push(trimmed);
    }

    flushList();
    flushPara();
    return blocks;
}

function renderInline(parts: InlinePart[], keyPrefix: string): React.ReactNode[] {
    return parts.map((part, i) => {
        const key = `${keyPrefix}-${i}`;
        switch (part.type) {
            case 'bold':
                return <strong key={key}>{part.value}</strong>;
            case 'italic':
                return <em key={key}>{part.value}</em>;
            case 'code':
                return <code key={key} className="office-sidebar__inline-code">{part.value}</code>;
            default:
                return <React.Fragment key={key}>{part.value}</React.Fragment>;
        }
    });
}

interface OfficeMessageContentProps {
    content: string;
    /** Use structured formatting (assistant). Plain whitespace for user. */
    formatted?: boolean;
}

const OfficeMessageContent: React.FC<OfficeMessageContentProps> = ({
    content,
    formatted = false,
}) => {
    const blocks = useMemo(() => {
        if (!formatted) return null;
        return parseBlocks(normalizeAssistantContent(content));
    }, [content, formatted]);

    if (!formatted || !blocks) {
        return (
            <div className="office-sidebar__message-content office-sidebar__message-content--plain">
                {content}
            </div>
        );
    }

    return (
        <div className="office-sidebar__message-content office-sidebar__message-content--rich">
            {blocks.map((block, i) => {
                if (block.type === 'paragraph') {
                    return (
                        <p key={`p-${i}`} className="office-sidebar__md-p">
                            {renderInline(block.parts, `p-${i}`)}
                        </p>
                    );
                }
                if (block.type === 'ol') {
                    return (
                        <ol key={`ol-${i}`} className="office-sidebar__md-ol">
                            {block.items.map((item, j) => (
                                <li key={`ol-${i}-${j}`}>{renderInline(item, `ol-${i}-${j}`)}</li>
                            ))}
                        </ol>
                    );
                }
                return (
                    <ul key={`ul-${i}`} className="office-sidebar__md-ul">
                        {block.items.map((item, j) => (
                            <li key={`ul-${i}-${j}`}>{renderInline(item, `ul-${i}-${j}`)}</li>
                        ))}
                    </ul>
                );
            })}
        </div>
    );
};

export default OfficeMessageContent;
